import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { UrlService } from '../url/url.service';
import { cacheOrExecute } from '../../utils';
import { AppConfig, ParamsModel } from '@boels/models';
import { WindowRef } from '../window/window-ref';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  protected http = inject(HttpClient);
  protected urlService = inject(UrlService);
  protected winRef = inject(WindowRef);
  protected environment = inject<AppConfig>('ENVIRONMENT' as any);

  get<T>(
    path: string,
    httpParams?: ParamsModel,
    httpHeaders?: HttpHeaders | undefined,
  ): Observable<T> {
    const { url, params } = this.prepareUrl(path, httpParams);
    return this.getCacheOrExecute(url, params, httpHeaders);
  }

  delete<T>(
    path: string,
    httpParams?: ParamsModel,
    httpHeaders?: HttpHeaders | undefined,
    contentType = 'application/x-www-form-urlencoded',
  ): Observable<T> {
    const { url, params } = this.prepareUrl(path, httpParams);
    if (!httpHeaders) httpHeaders = new HttpHeaders();

    httpHeaders = httpHeaders.set('Content-Type', contentType);
    return this.http.delete<T>(url, { params, headers: httpHeaders });
  }

  patch<T>(
    path: string,
    body: unknown,
    httpParams?: ParamsModel,
    httpHeaders?: HttpHeaders | undefined,
  ): Observable<T> {
    const { url, params } = this.prepareUrl(path, httpParams);
    return this.http.patch<T>(url, body, { params, headers: httpHeaders });
  }

  post<T>(
    path: string,
    body: unknown,
    httpParams?: ParamsModel,
    headers?: HttpHeaders,
  ): Observable<T> {
    const { url, params } = this.prepareUrl(path, httpParams);
    return this.http.post<T>(url, body, { params, headers });
  }

  put<T>(
    path: string,
    body: unknown,
    httpParams?: ParamsModel,
    headers?: HttpHeaders,
  ): Observable<T> {
    const { url, params } = this.prepareUrl(path, httpParams);
    return this.http.put<T>(url, body, { params, headers });
  }

  putWithResponse(
    path: string,
    body: never | null,
    options: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe: 'response';
      context?: HttpContext;
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    },
  ): Observable<HttpResponse<any>> {
    const { url } = this.prepareUrl(path);
    return this.http.put(url, body, options);
  }

  protected getCacheOrExecute<T>(
    url: string,
    params: HttpParams | undefined,
    headers?: HttpHeaders | undefined,
  ): Observable<T> {
    const cacheKey = `${url}:${params?.toString()}`;
    if (
      !this.environment.caching?.api?.enabled ||
      this.winRef.isBrowser() ||
      ['carts', 'search'].includes(cacheKey)
    )
      return this.http.get<T>(url, { headers: headers, params: params });

    return cacheOrExecute<T>(
      cacheKey,
      () => this.http.get<T>(url, { headers: headers, params: params }),
      this.environment.caching.api.ttl,
    );
  }

  protected prepareUrl(
    relativeOrAbsoluteUrl?: string,
    httpParams?: ParamsModel,
  ): { url: string; params?: HttpParams } {
    const { prefix, baseUrl } = this.environment.backend.occ;
    let params;

    if (httpParams) {
      params = this.formatParams(httpParams);
    }

    let url: string;
    if (relativeOrAbsoluteUrl && this.isUrlAbsolute(relativeOrAbsoluteUrl)) {
      url = relativeOrAbsoluteUrl;
    } else {
      url = this.urlService.combineUri(baseUrl, prefix, relativeOrAbsoluteUrl);
    }

    return {
      url,
      params,
    };
  }
  protected isUrlAbsolute(url: string) {
    return url.indexOf('://') > 0 || url.indexOf('//') === 0;
  }
  protected formatParams(httpParams: ParamsModel): HttpParams {
    let params = new HttpParams();

    httpParams.forEach((param: Array<string | number>) => {
      params = params.append(`${param[0]}`, param[1]);
    });

    return params;
  }
}
